import { isCacheExpired } from 'config/cache.config';
import dayjs from 'dayjs';
import { put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { bookingActions } from 'reduxStore/bookings/slice';
import { categoriesActions } from 'reduxStore/categories/slice';
import { accessLogsActions } from 'reduxStore/accessLogs/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { defaultCategoriesActions } from 'reduxStore/defaultCategories/slice';
import { devicesActions } from 'reduxStore/devices/slice';
import { floorsActions } from 'reduxStore/floors/slice';
import { lockersLogsActions } from 'reduxStore/lockersLogs/slice';
import { maintenanceActions } from 'reduxStore/maintenance/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { sitesActions } from 'reduxStore/sites/slice';
import { teamsActions } from 'reduxStore/teams/slice';
import { appActions } from './slice';

// This is a global listener that listens for all actions
// allows us to listen for actions and load missing resources due to errors or any other reason.
// allows us to perform side effects resulting from the multiple actions.
export function* globalSagaListener() {
	// Get the state of the application
	const state = yield select();

	// 1: prepare the user related Data and close the userLoadingComplete state
	// Check if user is logged and profile is loaded
	const { isLoggedIn, profileLoadingComplete } = state.Auth;

	// Check if the workspaces is loaded
	const { organisationLoadingComplete } = state.Organisations;

	// Check if the default settings are loaded
	const { personDefaultSettingsLoadingComplete, orgDefaultSettingsLoadingComplete } = state.DefaultSettings;

	// Check if the settings are loaded for the user and organization
	const { personSettingsLoadingComplete, orgSettingsLoadingComplete } = state.Settings;

	// Get the state of the user loading complete from the state
	const { userLoadingComplete } = state.App;

	// Check if the user is logged in and the user loading is not complete
	if (!userLoadingComplete) {
		// check if all the required data is loaded
		if (
			profileLoadingComplete &&
			isLoggedIn &&
			organisationLoadingComplete &&
			personSettingsLoadingComplete &&
			orgSettingsLoadingComplete &&
			personDefaultSettingsLoadingComplete &&
			orgDefaultSettingsLoadingComplete
		) {
			// If all the data is loaded, then set the user resource loading complete
			yield put(appActions.setUserLoadingComplete());

			//* Once the user's profile is loaded, then we can send user to the dashboard
			//* ?This is understood that this is a temp for now as we work with the settings to load the correct dashboard user is part of,
			//* ?therefore we are sending user to the access dashboard until we implement the functionality to look up the settings and deploy restricted loading etc.
			//* ?The restricted loading will include preventing user from switching the dashboard if the workspace is not configured for the respective dashboard.
			// ? This logic has moved to the Dashboard Chooser.
			// ? All the logic to decide which dashboard to redirect the user
			// ? to need to be implemented in the Dashboard Chooser
			// yield put(appActions.switchDashboard('access'));

			//Based on the current dashboard selection, we can load the dashboard data
			const selectedDashboard = yield select((state) => state.App.dashboard.selected);

			if (selectedDashboard === 'access') {
				yield put(appActions.loadAccessDashboardData());
			}
			if (selectedDashboard === 'lockers') {
				yield put(appActions.loadLockersDashboardData());
			}
		}
	}
}
// Switch listener when the user changes the dashboard
export function* switchDashboard(action) {
	// Check if the user has selected the access dashboard
	if (action.payload === 'access') {
		yield put(appActions.loadAccessDashboardData());
	}
	// Check if the user has selected the lockers dashboard
	if (action.payload === 'lockers') {
		yield put(appActions.loadLockersDashboardData());
	}
}
// Load the access dashboard data
export function* loadAccessDashboardData() {
	const state = yield select();

	//✅ Get the global states to check if the data is loaded
	const { orgID } = state.Auth.selectedProfile;

	// Permissions State
	const {
		data: permissionsData,
		permissionsLoadingComplete,
		loading: loadingPermissions,
		timeStamp: permissionsTimeStamp
	} = state.Permissions;

	// Sites State
	const { data: sitesData, sitesLoadingComplete, loading: sitesLoading, timeStamp: sitesTimeStamp } = state.Sites;

	// Floors State
	const {
		data: floorsData,
		floorsLoadingComplete,
		loading: floorsLoading,
		timeStamp: floorsTimeStamp
	} = state.Floors;

	// People State
	const {
		data: peopleData,
		peopleLoadingComplete,
		loading: loadingPeople,
		timeStamp: peopleTimeStamp
	} = state.People;

	// Teams State
	const { data: teamsData, teamsLoadingComplete, loading: teamsLoading, timeStamp: teamsTimeStamp } = state.Teams;

	// Default Categories
	const { loading: loadingDefaultCategories, loadingComplete: defaultCategoriesLoadingComplete } =
		state.DefaultCategories;

	// Categories State
	const {
		data: categoriesData,
		categoriesLoadingComplete,
		loading: loadingCategories,
		timeStamp: categoriesTimeStamp
	} = state.Categories;

	// Devices State
	const {
		data: devicesData,
		devicesLoadingComplete,
		loading: loadingDevices,
		timeStamp: devicesTimeStamp
	} = state.Devices;

	// Devices Groups State
	const {
		data: AssetGroupsData,
		assetGroupsLoadingComplete,
		loading: loadingAssetGroups,
		timeStamp: assetGroupsTimeStamp
	} = state.AssetGroups;

	// Credentials State
	const {
		data: credentialsData,
		credentialsLoadingComplete,
		loading: loadingCredentials,
		timeStamp: credentialsTimeStamp,
		// Credential Access State (consolidated in Credentials slice)
		access: credentialAccessData,
		credentialAccessLoadingComplete,
		loadingCredentialAccess,
		accessTimestamp: credentialAccessTimeStamp
	} = state.Credentials;

	// Access Logs State
	const {
		data: accessLogsData,
		loading: loadingAccessLogs,
		timeStamp: accessLogsTimeStamp,
		completeLogsData: accessLogsLoadingComplete
	} = state.AccessLogs;

	//IF Permissions data is expired or not loaded, then load the permissions data
	if (
		isCacheExpired(permissionsTimeStamp, 1) ||
		(!permissionsLoadingComplete && !loadingPermissions) ||
		!(orgID in permissionsData)
	) {
		yield put(permissionsActions.loadPermissions());
	}

	// If Sites cache data is expired or not loaded, then load the sites data
	if (isCacheExpired(sitesTimeStamp, 1) || (!sitesLoadingComplete && !sitesLoading) || !(orgID in sitesData)) {
		yield put(sitesActions.loadSites());
	}

	// If Floors cache data is expired or not loaded, then load the floors data
	if (isCacheExpired(floorsTimeStamp, 1) || (!floorsLoadingComplete && !floorsLoading) || !(orgID in floorsData)) {
		yield put(floorsActions.loadFloors());
	}

	// If People cache data is expired or not loaded, then load the people data
	if (isCacheExpired(peopleTimeStamp, 1) || (!peopleLoadingComplete && !loadingPeople) || !(orgID in peopleData)) {
		yield put(peopleActions.loadPerson());
	}

	//If Teams cache data is expired or not loaded, then load the teams data
	if (isCacheExpired(teamsTimeStamp, 1) || (!teamsLoadingComplete && !teamsLoading) || !(orgID in teamsData)) {
		yield put(teamsActions.loadTeams());
	}

	// If Default Categories cache data is expired or not loaded, then load the Default Categories Data
	if (!defaultCategoriesLoadingComplete && !loadingDefaultCategories) {
		yield put(defaultCategoriesActions.loadDefaultCategories());
	}

	//If Categories data is expired or not loaded, then load the categories data
	if (
		isCacheExpired(categoriesTimeStamp, 1) ||
		(!categoriesLoadingComplete && !loadingCategories) ||
		!(orgID in categoriesData)
	) {
		yield put(categoriesActions.loadCategories());
	}

	//If Devices data is expired or not loaded, then load the devices data
	if (
		isCacheExpired(devicesTimeStamp, 1) ||
		(!devicesLoadingComplete && !loadingDevices) ||
		!(orgID in devicesData)
	) {
		yield put(devicesActions.loadDevices());
	}

	//If Devices Groups data is expired or not loaded, then load the devices groups data
	if (
		isCacheExpired(assetGroupsTimeStamp, 1) ||
		(!assetGroupsLoadingComplete && !loadingAssetGroups) ||
		!(orgID in AssetGroupsData)
	) {
		yield put(assetGroupActions.loadGroups());
	}

	//If Credentials data is expired or not loaded, then load the credentials data
	if (
		isCacheExpired(credentialsTimeStamp, 1) ||
		(!credentialsLoadingComplete && !loadingCredentials) ||
		!(orgID in credentialsData)
	) {
		yield put(credentialsActions.loadCredentials());
	}

	//If Credential Access data is expired or not loaded, then load the credential access data
	if (
		isCacheExpired(credentialAccessTimeStamp, 1) ||
		(!credentialAccessLoadingComplete && !loadingCredentialAccess) ||
		!(orgID in credentialAccessData)
	) {
		yield put(credentialsActions.getCredentialAccess());
	}

	// If Access Logs data is expired or not loaded, then load the access logs data
	if (
		isCacheExpired(accessLogsTimeStamp, 1) ||
		(!accessLogsLoadingComplete && !loadingAccessLogs) ||
		!(orgID in accessLogsData)
	) {
		yield put(accessLogsActions.loadAccessLogs({ orgID }));
	}
}

// Load the lockers dashboard data
export function* loadLockersDashboardData() {
	const state = yield select();

	//✅ Get the global states to check if the data is loaded
	const { orgID } = state.Auth.selectedProfile;

	// Sites State
	const { data: sitesData, sitesLoadingComplete, loading: sitesLoading, timeStamp: sitesTimeStamp } = state.Sites;

	// Floors State
	const {
		data: floorsData,
		floorsLoadingComplete,
		loading: floorsLoading,
		timeStamp: floorsTimeStamp
	} = state.Floors;

	// Categories State
	const {
		data: categoriesData,
		categoriesLoadingComplete,
		loading: loadingCategories,
		timeStamp: categoriesTimeStamp
	} = state.Categories;

	// Devices State
	const {
		data: devicesData,
		devicesLoadingComplete,
		loading: loadingDevices,
		timeStamp: devicesTimeStamp
	} = state.Devices;

	// Bookings State
	const {
		data: bookingsData,
		bookingsLoadingComplete,
		loading: loadingBooking,
		timeStamp: bookingTimeStamp
	} = state.Bookings;

	// People State
	const {
		data: peopleData,
		peopleLoadingComplete,
		loading: loadingPeople,
		timeStamp: peopleTimeStamp
	} = state.People;

	//Logs Lockers V2
	const {
		data: lockersLogs,
		completeLogsData,
		loading: lockersLogsLoading,
		timeStamp: lockersLogsTimeStamp
	} = state.LockersLogs;

	// Maintenance Logs State
	const {
		data: maintenanceLogs,
		maintenanceLoadingComplete,
		loading: maintenanceLogsLoading,
		timeStamp: maintenanceLogsTimeStamp
	} = state.Maintenance;

	// If Sites cache data is expired or not loaded, then load the sites data
	if (isCacheExpired(sitesTimeStamp, 1) || (!sitesLoadingComplete && !sitesLoading) || !(orgID in sitesData)) {
		yield put(sitesActions.loadSites());
	}

	// If Floors cache data is expired or not loaded, then load the floors data
	if (isCacheExpired(floorsTimeStamp, 1) || (!floorsLoadingComplete && !floorsLoading) || !(orgID in floorsData)) {
		yield put(floorsActions.loadFloors());
	}

	//If Categories data is expired or not loaded, then load the categories data
	if (
		isCacheExpired(categoriesTimeStamp, 1) ||
		(!categoriesLoadingComplete && !loadingCategories) ||
		!(orgID in categoriesData)
	) {
		yield put(categoriesActions.loadCategories());
	}

	//If Devices data is expired or not loaded, then load the devices data
	if (
		isCacheExpired(devicesTimeStamp, 1) ||
		(!devicesLoadingComplete && !loadingDevices) ||
		!(orgID in devicesData)
	) {
		yield put(devicesActions.loadDevices());
	}

	// If Bookings data is expired or not loaded, then load the bookings data
	if (
		isCacheExpired(bookingTimeStamp, 1) ||
		(!bookingsLoadingComplete && !loadingBooking) ||
		!(orgID in bookingsData)
	) {
		yield put(bookingActions.loadBookings());
	}

	// If People cache data is expired or not loaded, then load the people data
	if (isCacheExpired(peopleTimeStamp, 1) || (!peopleLoadingComplete && !loadingPeople) || !(orgID in peopleData)) {
		yield put(peopleActions.loadPerson());
	}

	// If Lockers Logs data is expired or not loaded, then load the lockers logs data
	if (
		isCacheExpired(lockersLogsTimeStamp, 1) ||
		(!completeLogsData && !lockersLogsLoading) ||
		!(orgID in lockersLogs)
	) {
		yield put(
			lockersLogsActions.loadLockersLogs({
				fromDate: dayjs().subtract(3, 'month').startOf('day').toISOString(),
				toDate: dayjs().toISOString()
			})
		);
	}

	// If the maintenance logs data is expired or not loaded, then load the logs data
	if (
		isCacheExpired(maintenanceLogsTimeStamp, 1) ||
		(!maintenanceLoadingComplete && !maintenanceLogsLoading) ||
		!(orgID in maintenanceLogs)
	) {
		yield put(maintenanceActions.loadMaintenanceLog());
	}
}

// Listen for the dashboard switch and load the data when is access dashboard
export function* accessGlobalListener() {
	// Get the state of the application
	const state = yield select();

	// Listen for the dashboard switch
	const { sitesLoadingComplete } = state.Sites;
	const { floorsLoadingComplete } = state.Floors;
	const { peopleLoadingComplete } = state.People;
	const { teamsLoadingComplete } = state.Teams;
	const { categoriesLoadingComplete } = state.Categories;
	const { devicesLoadingComplete } = state.Devices;

	const { dashboardReady } = state.App;

	// Check if the access dashboard is loaded
	if (!dashboardReady.includes('access')) {
		if (
			sitesLoadingComplete &&
			floorsLoadingComplete &&
			peopleLoadingComplete &&
			teamsLoadingComplete &&
			categoriesLoadingComplete &&
			devicesLoadingComplete
		) {
			yield put(appActions.setAccessDashboardReady());
		}
	}

	// Listen for the dashboard switch for Lockers Dashboard
	const { bookingsLoadingComplete } = state.Bookings;
	const { completeLogsData } = state.LockersLogs;
	const { maintenanceLoadingComplete } = state.Maintenance;

	// Check if the lockers dashboard is loaded
	if (!dashboardReady.includes('lockers')) {
		if (
			sitesLoadingComplete &&
			floorsLoadingComplete &&
			categoriesLoadingComplete &&
			devicesLoadingComplete &&
			bookingsLoadingComplete &&
			peopleLoadingComplete &&
			completeLogsData &&
			maintenanceLoadingComplete
		) {
			yield put(appActions.setLockersDashboardReady());
		}
	}
}

// The main export for the saga
export default function* appSagas() {
	yield takeLeading('*', globalSagaListener);
	yield takeLeading('*', accessGlobalListener);
	yield takeLatest(appActions.switchDashboard, switchDashboard);
	yield takeLatest(appActions.loadAccessDashboardData, loadAccessDashboardData);
	yield takeLatest(appActions.loadLockersDashboardData, loadLockersDashboardData);
}
